import React from "react";
import { DateTime } from "luxon";
import { amountFormatter } from "services";
import { Separator } from "../../../../shadcn/components/ui/separator";
import { Button } from "../../../../shadcn/components/ui/button";

interface EventDetailsPanelProps {
  data: {
    reserve_price: string;
    bidding_increment_amount: string;
    extend_time_at?: string;
    listing_id?: string;
    borrower_name?: string;
    property_dump?: {
      description?: string;
    };
  };
  latestBidAmount: number | string;
  latestBid?: {
    number_of_extension?: number;
  };
  endTime: number;
  status: string;
  biddingType: string;
  bank: string;
  state: string;
  city: string;
  setOpenModal: (open: boolean) => void;
}

export default function EventDetailsPanel({
  data,
  latestBidAmount,
  latestBid,
  endTime,
  status,
  biddingType,
  bank,
  state,
  city,
  setOpenModal,
}: EventDetailsPanelProps) {
  const calculateNextBid = () => {
    if (!latestBidAmount) return "₹ 00";
    const latestBidValue =
      typeof latestBidAmount === "string"
        ? parseInt(latestBidAmount)
        : latestBidAmount;
    let nextBid;
    if (biddingType === "rp") {
      if (!latestBid && latestBidValue === parseInt(data.reserve_price)) {
        nextBid = latestBidValue;
      } else {
        nextBid = latestBidValue + parseInt(data.bidding_increment_amount);
      }
    } else {
      nextBid = latestBidValue + parseInt(data.bidding_increment_amount);
    }

    return `₹ ${amountFormatter(nextBid)} or above`;
  };

  const formattedEndTime = endTime
    ? `${DateTime.fromMillis(endTime)
        .setZone("Asia/Kolkata")
        .toFormat("hh:mm:ss a")} (IST)`
    : "00:00:00 PM (IST)";

  return (
    <div className="bg-orange-50 border h-full border-orange-200 rounded-xl p-4 space-y-2.5">
      <div>
        <div className="flex justify-between mb-2">
          <h3 className="text-sm font-semibold mb-2">Event Details</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenModal(true)}
            disabled={[
              "end",
              "rejected",
              "cancelled",
              "pending_bid_approval",
              "holding_bid_approval",
              "success",
              "failed",
              "bid_rejected",
            ].includes(status)}
            className="rounded-lg bg-red-500 text-white hover:bg-red-600 hover:text-white border-0 h-6 px-2 text-xs flex items-center"
          >
            Cancel Event
          </Button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Last Bid</span>
            <span className="font-semibold text-[#4DBB3E]">
              ₹ {amountFormatter(latestBidAmount)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Next Possible Bid</span>
            <span className="font-medium text-xs">{calculateNextBid()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Reserve Price</span>
            <span className="font-medium text-xs">
              ₹ {amountFormatter(data.reserve_price)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Bid Increment</span>
            <span className="font-medium text-xs">
              ₹ {amountFormatter(data.bidding_increment_amount)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Auction Ends At</span>
            <span className="font-medium text-xs">{formattedEndTime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">No. of Extensions</span>
            <span className="font-medium text-xs">
              {latestBid?.number_of_extension || "0"}
            </span>
          </div>
        </div>
      </div>

      <Separator className="bg-orange-200 my-2" />

      <div>
        <h3 className="text-sm font-semibold mb-2">Property Details</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Bank</span>
            <span className="font-medium capitalize text-xs">
              {bank || "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">State</span>
            <span className="font-medium capitalize text-xs">
              {state || "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">City</span>
            <span className="font-medium capitalize text-xs">
              {city || "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Borrower name</span>
            <span className="font-medium capitalize text-xs">
              {data?.borrower_name || "N/A"}
            </span>
          </div>
          <div className="">
            <span className="text-gray-600 mr-4">Description</span>
            <span className="font-medium capitalize text-xs">
              {data?.property_dump?.description || "N/A"}
            </span>
          </div>
        </div>
      </div>

      <Separator className="bg-orange-200 my-2" />

      {/* <p className="text-xs text-gray-500">
        Note: IP-based alerts are for banker's internal risk checks only and are
        not visible to bidders.
      </p> */}
    </div>
  );
}
